import React, { FC, useContext, useEffect, useMemo } from 'react';
import { UUID } from '../../types/uuid';
import { LazyUsersContext, TLazyUserInfo } from '../../contexts/LazyUsersContext';
import Link from '@mui/material/Link';
import { GuardedRouteLink } from '../../resources/GuardedRouteLink';
import { keyframes, styled } from '@mui/material';
import { EOrganizationSelectionStatus, OrganizationContext } from '../../contexts/OrganizationContext';
import { LazyFailed } from '../LazyFailed/LazyFailed';
import { IUserContext, UserContext } from '../../contexts/UserContext';

interface IProps {
  id: UUID;
  Render?: FC<{ user: TLazyUserInfo }>;
}

const placeholderAnimation = keyframes({
  '0%': {
    backgroundPositionX: '0%',
  },
  '100%': {
    backgroundPositionX: '100px',
  },
});

const LazyPlaceholder = styled('span')({
  // background: '#ffffff20',
  backgroundSize: '50px',
  backgroundImage: 'linear-gradient(90deg, #ffffff20 0%, #ffffff2a 50%, #ffffff20 100%)',
  borderRadius: 4,
  color: 'transparent',
  animation: placeholderAnimation,
  animationDuration: '3s',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'linear',
  '&:before': {
    content: '"...................."',
  },
});

const DefaultLazyUserRenderer: FC<{ user: TLazyUserInfo }> = ({ user }) => {
  const { organization, selectionStatus } = useContext(OrganizationContext);

  if (selectionStatus === EOrganizationSelectionStatus.Injected) {
    if (organization) {
      return (
        <Link
          component={GuardedRouteLink}
          route={'UserOrganization'}
          params={[organization?.name, user.id]}
          showFallback
        >
          {user.first_name} {user.last_name}
        </Link>
      );
    } else {
      return (
        <Link component={GuardedRouteLink} route={'User'} params={[user.id]} showFallback>
          {user.first_name} {user.last_name}
        </Link>
      );
    }
  }

  return (
    <Link component={GuardedRouteLink} route={'User'} params={[user.id]} showFallback>
      {user.first_name} {user.last_name}
    </Link>
  );
};

export const LazyUser: FC<IProps> = ({ id, Render = DefaultLazyUserRenderer }) => {
  const { data: users, requestData: requestUserData, failedData: failedUsers } = useContext(LazyUsersContext);

  const lcId = id?.toLowerCase();

  useEffect(() => {
    requestUserData(lcId);
  }, [lcId, requestUserData]);

  const user = users[lcId];
  const failed = !user && failedUsers.includes(lcId);

  return user ? <Render user={user} /> : failed ? <LazyFailed id={lcId} /> : <LazyPlaceholder />;
};

export const LazyUserContextProvider: FC<{ id?: UUID }> = ({ id, children }) => {
  const { data: users, requestData: requestUserData } = useContext(LazyUsersContext);

  const lcId = id?.toLowerCase();

  useEffect(() => {
    if (lcId) {
      requestUserData(lcId);
    }
  }, [lcId, requestUserData]);

  const user = lcId ? users[lcId] : undefined;

  const contextValue = useMemo<IUserContext>(() => {
    if (user) {
      return {
        user: {
          ...user,
          phone_number: null,
          verified: true,
          default_organization: null,
          created_at: '',
          updated_at: '',
        },
        userId: user.id,
      };
    }
    return { user: null, userId: null };
  }, [user]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};
